'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FinderData, LocationCongregation, LocationGroup } from '@/lib/types';
import { campusSvgMap } from '@/lib/types';
import { fetchFinderData, fetchHomeCampus, fetchLocations, setHomeCampus } from '@/lib/api';
import { campusSlug, getSelectedCampusId, onCampusChange, setSelectedCampus } from '@/lib/campus';
import { buildCampusIndex } from '@/lib/campusIndex';
import {
  EMPTY_FILTERS,
  applyRawParams,
  filterOpportunities,
  parseEmbedOptions,
  syncFiltersToUrl,
  type FilterState,
} from '@/lib/filters';
import { pluralOpportunities } from '@/lib/format';
import { CampusDropdown } from './CampusDropdown';
import { CampusSvgProvider } from './CampusIcon';
import { ServeCard } from './ServeCard';
import { FilterBar } from './FilterBar';
import { FilterSheet } from './FilterSheet';
import { ServeCardSkeleton, FilterBarSkeleton } from './Skeletons';

export interface ServeFinderProps {
  apiBaseUrl: string;
  /**
   * Parameters baked into the embed (`data-params="campus=troy"`, or
   * data-* attributes). Filters apply over the visitor's default campus,
   * under the page URL; embed constraints (include/exclude lists, limit,
   * hide-filters) are fixed.
   */
  embedParams?: [string, string][];
  /** Override the details link base (stock `targeturl`) */
  detailsUrl?: string;
  /** Stock `showfulladdress` on the sheet */
  showFullAddress?: boolean;
}

type Status = 'loading' | 'ready' | 'error';

const DEFAULT_DETAILS_URL = 'https://woodsidebible.org/serve/';
const DEFAULT_FINDER_URL = 'https://woodsidebible.org/serve/';

function urlEntries(): [string, string][] {
  if (typeof window === 'undefined') return [];
  try {
    return Array.from(new URLSearchParams(window.location.search).entries());
  } catch {
    return [];
  }
}

export function ServeFinder({
  apiBaseUrl,
  embedParams = [],
  detailsUrl,
  showFullAddress = false,
}: ServeFinderProps) {
  // A link can name its roles (`/serve/?ids=4034,4035`), as can an embed,
  // whose list wins. A list is the whole answer, so it skips the default campus.
  const urlIds = useMemo(() => parseEmbedOptions(urlEntries()).ids, []);
  const embed = useMemo(() => {
    const e = parseEmbedOptions(embedParams);
    return e.ids.length > 0 || urlIds.length === 0 ? e : { ...e, ids: urlIds };
  }, [embedParams, urlIds]);
  const [data, setData] = useState<FinderData | null>(null);
  const [status, setStatus] = useState<Status>('loading');
  const [locations, setLocations] = useState<LocationGroup[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [homeCampus, setHomeCampusState] = useState<number | null | undefined>(undefined);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const initialised = useRef(false);

  // Load everything once. The catalogue is required; campuses and the
  // visitor's home campus are extras that degrade quietly. Filtering
  // happens in memory from here on.
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    setStatus('loading');

    fetchFinderData(apiBaseUrl, signal)
      .then((d) => {
        setData(d);
        setStatus('ready');
      })
      .catch((err) => {
        if (signal.aborted) return;
        console.error('Serve finder failed to load opportunities:', err);
        setStatus('error');
      });

    fetchLocations(apiBaseUrl, signal)
      .then((l) => setLocations(l))
      .catch((err) => {
        if (!signal.aborted) console.warn('Serve finder: campuses unavailable', err);
      })
      .finally(() => {
        if (!signal.aborted) setLocationsLoading(false);
      });

    fetchHomeCampus(apiBaseUrl, signal).then((id) => {
      if (!signal.aborted) setHomeCampusState(id);
    });

    return () => controller.abort();
  }, [apiBaseUrl]);

  // The household's own sign-ups, whenever the sign-in state changes.

  const campusIndex = useMemo(
    () => buildCampusIndex(locations, data?.options.campuses ?? []),
    [locations, data]
  );

  // Initial filters, once the catalogue, the home-campus lookup and the
  // campus list are all in: signed-in home campus > campus cookie < embed
  // params < page URL. An embed that hides the campus control gets no
  // default campus, so a ministry page lists every campus by default.
  useEffect(() => {
    if (!data || homeCampus === undefined || locationsLoading || initialised.current) return;
    initialised.current = true;
    const known = (id: number | null) =>
      id !== null && data.options.campuses.some((c) => c.id === id);
    const resolve = (v: string) => campusIndex.resolve(v);

    let next: FilterState = { ...EMPTY_FILTERS };
    if (!embed.hideCampus && embed.ids.length === 0) {
      const cookieCampus = getSelectedCampusId();
      if (known(homeCampus)) next = { ...next, campus: homeCampus };
      else if (known(cookieCampus)) next = { ...next, campus: cookieCampus };
    }
    next = applyRawParams(next, embedParams, resolve);
    if (!embed.hideFilters) next = applyRawParams(next, urlEntries(), resolve);
    setFilters(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, homeCampus, locationsLoading]);

  // Follow the site-wide campus picker without a reload.
  useEffect(() => {
    if (embed.hideCampus) return;
    return onCampusChange((id) => {
      setFilters((f) => (f.campus === id ? f : { ...f, campus: id }));
    });
  }, [embed.hideCampus]);

  // Keep the URL shareable (not for fixed embeds, which are not the page's subject).
  useEffect(() => {
    if (!initialised.current || embed.hideFilters) return;
    syncFiltersToUrl(filters, campusIndex.slugFor);
  }, [filters, campusIndex, embed.hideFilters]);

  const svgMap = useMemo(() => campusSvgMap(locations), [locations]);
  const hits = useMemo(
    () => (data ? filterOpportunities(data.opportunities, filters, embed) : []),
    [data, filters, embed]
  );
  const links = {
    details: detailsUrl || data?.links.details || DEFAULT_DETAILS_URL,
    finder: data?.links.finder || DEFAULT_FINDER_URL,
  };

  // Picking a campus in the dropdown behaves like the site header's picker:
  // cookie + event for every other widget on the page, and the MP home
  // campus when signed in. "All campuses" only widens this list.
  const handleCampusSelect = useCallback(
    (cong: LocationCongregation | null, svgUrl: string | null) => {
      setFilters((f) => ({ ...f, campus: cong ? cong.id : null }));
      if (!cong) return;
      setSelectedCampus({
        congregationId: cong.id,
        name: cong.name,
        slug: cong.campus_slug ?? campusSlug(cong.name),
        svgUrl,
      });
      void setHomeCampus(apiBaseUrl, cong.id);
    },
    [apiBaseUrl]
  );

  if (status === 'error') {
    return (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-gray-200 bg-white px-6 py-14 text-center">
        <p className="text-lg font-bold text-[#1c2b39]">
          We couldn't load serve opportunities right now.
        </p>
        <p className="max-w-md text-sm text-gray-600">Please try again in a moment.</p>
      </div>
    );
  }

  const ready = status === 'ready' && data !== null;

  return (
    <CampusSvgProvider value={svgMap}>
      <div className="flex flex-col gap-5 text-[#1c2b39]">
        {!embed.hideFilters &&
          (!ready ? (
            <FilterBarSkeleton />
          ) : (
            <FilterBar
              options={data.options}
              filters={filters}
              onChange={setFilters}
              onOpenFilters={() => setFiltersOpen(true)}
              hideSearch={embed.hideSearch}
              campusControl={
                embed.hideCampus ? undefined : (
                  <CampusDropdown
                    locations={locations}
                    selectedId={filters.campus}
                    onSelect={handleCampusSelect}
                    loading={locationsLoading}
                  />
                )
              }
            />
          ))}

        {!ready ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true">
            <ServeCardSkeleton />
            <ServeCardSkeleton />
            <ServeCardSkeleton />
          </div>
        ) : (
          <>
            {!embed.hideFilters && (
              <p className="text-sm text-gray-500" aria-live="polite">
                {hits.length === 0
                  ? 'No opportunities match your filters'
                  : pluralOpportunities(hits.length)}
              </p>
            )}

            {hits.length === 0 ? (
              <div className="flex flex-col items-center gap-4 rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center">
                <p className="text-lg font-bold text-[#1c2b39]">Nothing here yet</p>
                <p className="max-w-md text-sm text-gray-600">
                  Try widening your filters or picking another campus.
                </p>
                {!embed.hideFilters && (
                  <button
                    type="button"
                    onClick={() => setFilters({ ...EMPTY_FILTERS })}
                    className="rounded-full bg-gray-100 px-5 py-2.5 text-sm font-bold text-[#1c2b39] transition-all hover:scale-105 active:scale-95"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {hits.map((o) => (
                  <ServeCard key={o.id} opportunity={o} detailsUrl={links.details} />
                ))}
              </div>
            )}
          </>
        )}

        {data && (
          <FilterSheet
            open={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            options={data.options}
            filters={filters}
            onChange={setFilters}
            count={hits.length}
            hideCampus={embed.hideCampus}
          />
        )}
      </div>
    </CampusSvgProvider>
  );
}
